<template>
	<Teleport to="body" :disabled="!reference">
		<Transition name="fade">
			<div
				v-if="visible"
				ref="panelRef"
				data-shopping-list-picker-panel
				:class="resolvedPanelClass"
				:style="reference ? floatingStyles : undefined"
			>
				<UseFocusTrap :options="focusTrapOptions">
					<ProductShoppingListPicker @select="emit('close')" />
				</UseFocusTrap>
			</div>
		</Transition>
	</Teleport>
</template>

<script setup lang="ts">
	import {useFloating, offset, shift, autoUpdate} from '@floating-ui/vue';
	import {UseFocusTrap} from '@vueuse/integrations/useFocusTrap/component';
	import type {MaybeElementRef} from '@vueuse/core';

	interface Props {
		visible: boolean
		panelClass?: string
		reference?: MaybeElementRef
	}

	interface Emits {
		(e: 'close'): void
	}

	const ABSOLUTE_PANEL_CLASS = 'rem:max-h-[210px] overflow-y-auto shadow-md absolute bottom-[100%] translate-y-[-0.5rem] z-20 right-0 bg-white transition-all duration-300 flex flex-col rounded-2.5xl min-w-[16rem]';
	const FLOATING_PANEL_CLASS = 'rem:max-h-[210px] overflow-y-auto shadow-md z-popup bg-white transition-all duration-300 flex flex-col rounded-2.5xl min-w-[16rem]';

	const props = withDefaults(defineProps<Props>(), {panelClass: ABSOLUTE_PANEL_CLASS});

	const emit = defineEmits<Emits>();

	const panelRef = useTemplateRef<HTMLElement>('panelRef');
	const referenceEl = computed(() => toValue(props.reference));

	const resolvedPanelClass = computed(() => (
		props.reference ? FLOATING_PANEL_CLASS : props.panelClass
	));

	const {floatingStyles} = useFloating(referenceEl, panelRef, {
		placement: 'bottom-end',
		whileElementsMounted: autoUpdate,
		middleware: [offset(8), shift({padding: 20})],
	});

	const isOpen = computed({
		get: () => props.visible,
		set: (value) => {
			if (!value) {
				emit('close');
			}
		},
	});

	const focusTrapOptions = {
		immediate: true,
		allowOutsideClick: true,
		clickOutsideDeactivates: (event: MouseEvent | TouchEvent) => {
			const target = event.target;
			if (!(target instanceof Element)) {
				return true;
			}

			return !target.closest('[data-shopping-list-toggle]');
		},
		onDeactivate() {
			isOpen.value = false;
		},
	};
</script>
